import React, { memo, useMemo } from 'react';
import { get } from 'lodash-es';
import { getPublicationStateForMakeModel, isUsed } from 'site-modules/shared/utils/publication-states';
import { getUsedVehiclePriceRange } from 'site-modules/shared/utils/core-page/pricing';
import { getPriceRangeString } from 'site-modules/shared/utils/price-utils';
import { ESTIMATED_OFFER_DATA_KEYS } from 'site-modules/shared/constants/appraisal/estimate';

const BELOW_AVERAGE_ABBREVIATED = 'Below Avg.';

function getActionStartTrackingData(vehicle) {
  if (!vehicle) return null;

  return {
    make_id: vehicle?.make?.id,
    model_year_id: vehicle.modelYearId,
    model_link_code: vehicle?.model?.modelLinkCode,
    submodel_id: vehicle.id,
    publication_state: getPublicationStateForMakeModel(vehicle.pubStates),
  };
}

export function withHeaderContentDataAdapter(WrappedComponent) {
  function HeaderContentDataAdapter(props) {
    const {
      componentParams,
      msrpPriceRanges,
      estimatedPrice,
      editorialRating,
      vehicle,
      submodelAmounts,
      usedTmv,
      ...rest
    } = props;

    const { make, model, year, trim = {} } = componentParams;

    const msrpMinMaxPrices = useMemo(() => {
      if (!msrpPriceRanges) {
        return msrpPriceRanges;
      }

      const sortedPriceRanges = msrpPriceRanges.toSorted((a, b) => a - b);

      return {
        min: sortedPriceRanges[0],
        max: sortedPriceRanges[1],
      };
    }, [msrpPriceRanges]);

    const usedMinMaxTmv = useMemo(
      () =>
        usedTmv && {
          min: get(usedTmv, 'ROUGH.Current.totalWithOptions.usedTmvRetail', 0),
          max: get(usedTmv, 'OUTSTANDING.Current.totalWithOptions.usedTmvRetail', 0),
        },
      [usedTmv]
    );

    let vehiclePrice = '';
    if (vehicle && isUsed(vehicle.pubStates)) {
      const { min, max } = getUsedVehiclePriceRange({
        submodelAmounts,
        usedTmv: usedMinMaxTmv,
        submodelSlug: vehicle?.submodels?.slug,
      });
      vehiclePrice = getPriceRangeString(min, max);
    } else if (msrpMinMaxPrices?.min && msrpMinMaxPrices?.max) {
      vehiclePrice = getPriceRangeString(msrpMinMaxPrices.min, msrpMinMaxPrices.max);
    }

    const editorialRatingText = editorialRating
      ? `${
          editorialRating.rating_enum === ESTIMATED_OFFER_DATA_KEYS.BELOW_AVERAGE
            ? BELOW_AVERAGE_ABBREVIATED
            : editorialRating.rating_enum
        } Rating ${editorialRating.rating_number}/10`
      : '';

    const estimatedPriceText = estimatedPrice ? estimatedPrice.substring(estimatedPrice.indexOf('$')) : '';
    const actionStartTrackingData = useMemo(() => getActionStartTrackingData(vehicle), [vehicle]);

    return (
      <WrappedComponent
        vehiclePrice={vehiclePrice}
        estimatedPrice={estimatedPriceText}
        make={make.name}
        model={model.name}
        trim={trim.name}
        year={year}
        makeNiceName={make.value}
        modelNiceName={model.value}
        editorialRating={editorialRatingText}
        pubStates={vehicle?.pubStates}
        actionStartTrackingData={actionStartTrackingData}
        {...rest}
      />
    );
  }

  HeaderContentDataAdapter.displayName = 'HeaderContentDataAdapter';

  return memo(HeaderContentDataAdapter);
}
